/* eslint-disable complexity */
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { prepareBookWidgetFieldsMasterConfig } from '../../adapters/book-widget-fields-master-config/book-widget-fields-master-config.adapter';
import { BookWidgetStoreKey } from '../../constants/common.constants';
import { BOOK_WIDGET_ERROR_CODES } from '../../constants/error-codes.constants';
import { BookWidgetStore } from '../../models/states/book-widget-store.interfaces';
import { getBookWidgetAem, isAemStateReady } from './aem.selectors';
import {
  getCatalogApiFieldsConfig,
  isCatalogApiStateReady
} from './catalog-api.selectors';
import {
  getDefaultsLookupFieldsConfig,
  isDefaultLookupStateReady
} from './default-lookup.selectors';
import { getFeatureFlags } from './feature-flags.selectors';
import { isGeoLocationStateReady } from './geo-location.selectors';
import {
  getLoginDashboardFieldsConfig,
  isLoginDashboardStateReady
} from './login-dashboard.selectors';
import { isRetrieveSearchStateReady } from './retrieve-search.selectors';

const bookWidgetStoreFeatureSelector =
  createFeatureSelector<BookWidgetStore>(BookWidgetStoreKey);

const getBookWidgetState = createSelector(
  bookWidgetStoreFeatureSelector,
  (state) => state.bookWidget
);

export const getBookWidgetFields = createSelector(
  getBookWidgetState,
  ({ fields }) => fields
);

export const getBookWidgetFieldsErrorCount = createSelector(
  getBookWidgetState,
  ({ fieldsErrorCount }) => fieldsErrorCount
);

export const getStartFieldsValidationValue = createSelector(
  getBookWidgetState,
  ({ startFieldsValidation }) => startFieldsValidation
);

export const isBookWidgetReady = createSelector(
  isAemStateReady,
  isCatalogApiStateReady,
  isDefaultLookupStateReady,
  isGeoLocationStateReady,
  isRetrieveSearchStateReady,
  isLoginDashboardStateReady,
  (
    isAemStateReadyValue,
    isCatalogApiStateReadyValue,
    isDefaultLookupStateReadyValue,
    isGeoLocationStateReadyValue,
    isRetrieveSearchStateReadyValue,
    isLoginDashboardStateReadyValue
  ) =>
    isAemStateReadyValue &&
    isCatalogApiStateReadyValue &&
    isDefaultLookupStateReadyValue &&
    isGeoLocationStateReadyValue &&
    isRetrieveSearchStateReadyValue &&
    isLoginDashboardStateReadyValue
);

export const getBookWidgetFieldsMasterConfig = createSelector(
  getFeatureFlags,
  getDefaultsLookupFieldsConfig,
  getCatalogApiFieldsConfig,
  getLoginDashboardFieldsConfig,
  getBookWidgetAem,
  (
    featureFlags,
    defaultsLookupFieldsConfig,
    catalogApiFieldsConfig,
    loginDashboardFieldsConfig,
    bookWidgetAem
  ) =>
    prepareBookWidgetFieldsMasterConfig(
      featureFlags,
      defaultsLookupFieldsConfig,
      catalogApiFieldsConfig,
      loginDashboardFieldsConfig,
      bookWidgetAem
    )
);

export const getGuidedNavigationErrorMsg = createSelector(
  getBookWidgetFieldsErrorCount,
  getBookWidgetAem,
  (bookWidgetFieldsErrorCount, bookWidgetAem) => {
    const aemErrorObject =
      bookWidgetAem?.errorMessages?.[
        BOOK_WIDGET_ERROR_CODES.BOOK_FIELD_ERROR_GLOBAL_ERROR
      ];

    if (bookWidgetFieldsErrorCount) {
      if (bookWidgetFieldsErrorCount === 1) {
        return aemErrorObject?.singleMessage;
      }

      return aemErrorObject?.message.replace(
        '{{errorCount}}',
        bookWidgetFieldsErrorCount.toString()
      );
    }

    return null;
  }
);

export const getFlightShopTemplateType = createSelector(
  getBookWidgetState,
  ({ templateType }) => templateType
);

export const getBookWidgetFlexData = createSelector(
  getBookWidgetState,
  ({ bookWidgetFlexData }) => bookWidgetFlexData
);
